import { useState } from "react"

const CLOUDINARY_CLOUD_NAME = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME
const CLOUDINARY_UPLOAD_PRESET = process.env.NEXT_PUBLIC_CLOUDINARY_UPLOAD_PRESET

export type ResourceType = "image" | "video"

export interface UploadMediaParams {
    folderPath: string
    tags?: string[]
    file?: File
}

export interface CloudinaryUploadResponse {
    public_id: string
    secure_url: string
    tags?: string[]
    format: string
    original_filename: string
    bytes: number
}

interface CloudinaryUploadConfig {
    cloudName: string
    uploadPreset: string
    multiple: boolean
    theme: string
    showPoweredBy: boolean
    showAdvancedOptions: boolean
    sources: string[]
    styles: {
        palette: {
            window: string
            windowBorder: string
            tabIcon: string
            menuIcons: string
            textDark: string
            textLight: string
            link: string
            action: string
            inactiveTabIcon: string
            error: string
            inProgress: string
            complete: string
            sourceBg: string
        }
        frame: {
            background: string
        }
    }
    resourceType?: ResourceType
    clientAllowedFormats?: string[]
    maxFileSize?: number
    folder?: string
    tags?: string[]
}

const MEDIA_CONFIGS = {
    image: {
        resourceType: "image" as const,
        allowedFormats: ["png", "gif", "jpeg", "jpg", "webp"],
        maxFileSize: 10_000_000 // 10MB
    },
    video: {
        resourceType: "video" as const,
        allowedFormats: ["mp4", "mov", "avi"],
        maxFileSize: 50_000_000 // 50MB
    }
} as const

const DEFAULT_WIDGET_CONFIG: Omit<CloudinaryUploadConfig, "resourceType" | "clientAllowedFormats" | "maxFileSize"> = {
    cloudName: CLOUDINARY_CLOUD_NAME!,
    uploadPreset: CLOUDINARY_UPLOAD_PRESET!,
    multiple: false,
    theme: "minimal",
    showPoweredBy: false,
    showAdvancedOptions: false,
    sources: ["local", "url", "camera"],
    styles: {
        palette: {
            window: "#FFF",
            windowBorder: "#90A0B3",
            tabIcon: "#0E2F5A",
            menuIcons: "#5A616A",
            textDark: "#000000",
            textLight: "#FFFFFF",
            link: "#0078FF",
            action: "#FF620C",
            inactiveTabIcon: "#0E2F5A",
            error: "#F44235",
            inProgress: "#0078FF",
            complete: "#20B832",
            sourceBg: "#E4EBF1"
        },
        frame: {
            background: "#0E2F5B99"
        }
    }
}

const useMediaUpload = () => {
    const [isLoading, setIsLoading] = useState(false)

    if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
        throw new Error("Cloudinary environment variables are missing")
    }

    const uploadDirectly = async (
        file: File,
        resourceType: ResourceType,
        folderPath: string,
        tags: string[] = []
    ): Promise<CloudinaryUploadResponse> => {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET)
        formData.append("folder", folderPath)
        tags.forEach((tag) => formData.append("tags[]", tag))

        const response = await fetch(
            `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/upload`,
            {
                method: "POST",
                body: formData
            }
        )

        if (!response.ok) {
            throw new Error(`Upload failed with status: ${response.status}`)
        }

        return response.json()
    }

    const uploadWithWidget = (
        resourceType: ResourceType,
        folderPath: string,
        tags: string[] = []
    ): Promise<CloudinaryUploadResponse> => {
        return new Promise((resolve, reject) => {
            try {
                setIsLoading(true)

                const config = {
                    ...DEFAULT_WIDGET_CONFIG,
                    ...MEDIA_CONFIGS[resourceType],
                    folder: folderPath,
                    tags
                }

                const widget = window.cloudinary.createUploadWidget(config, (error, result) => {
                    if (error || !result || result.event !== "success") {
                        setIsLoading(false)
                        return
                    }

                    resolve(result.info)
                    setIsLoading(false)
                })

                widget.open()
            } catch (error) {
                setIsLoading(false)
                reject(error)
            }
        })
    }

    const uploadMedia = async ({ folderPath, tags = [], file }: UploadMediaParams, resourceType: ResourceType) => {
        if (file) {
            return uploadDirectly(file, resourceType, folderPath, tags)
        }
        return uploadWithWidget(resourceType, folderPath, tags)
    }

    const uploadImage = (params: UploadMediaParams) => uploadMedia(params, "image")
    const uploadVideo = (params: UploadMediaParams) => uploadMedia(params, "video")

    return {
        uploadImage,
        uploadVideo,
        isLoading
    }
}

export default useMediaUpload
